import './ChildAvatarHero.css';

/**
 * Child-avatar hero — the parent-shell equivalent of the coach
 * shell's BrainOrb. The visual centre of the parent's experience is
 * THEIR CHILD, not an AI agent.
 *
 * If the child has an avatar URL we render that; otherwise a
 * deterministic gradient with the child's initials. Gradient uses the
 * accent system + four-corner palette mixed by name-hash so different
 * children visually differ.
 */

export interface ChildAvatarHeroProps {
  child: {
    id: string;
    name: string;
    ageBand: string | null;
    clubName: string | null;
    teamName: string | null;
  };
  /** Smaller variant for the chooser strip. */
  size?: 'lg' | 'sm';
  /** Active state for the chooser. */
  active?: boolean;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Stable hash → 0..3 to pick a four-corner palette tint for the
 * gradient. Deterministic across sessions so the same child always
 * has the same avatar colour.
 */
function paletteIndex(name: string): 0 | 1 | 2 | 3 {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return (Math.abs(h) % 4) as 0 | 1 | 2 | 3;
}

export function ChildAvatarHero({ child, size = 'lg', active = true }: ChildAvatarHeroProps) {
  const inits = initials(child.name);
  const tone = paletteIndex(child.name);
  return (
    <div
      className="child-hero"
      data-size={size}
      data-active={active ? 'true' : 'false'}
      data-tone={tone}
      aria-label={`${child.name}, ${child.ageBand ?? ''} ${child.clubName ?? ''}`.trim()}
    >
      <div className="child-hero__avatar" aria-hidden="true">
        <span className="child-hero__initials">{inits}</span>
      </div>
      {size === 'lg' && (
        <div className="child-hero__meta">
          <div className="child-hero__name">{child.name}</div>
          <div className="child-hero__sub">
            {[child.ageBand, child.teamName, child.clubName].filter(Boolean).join(' · ') || 'Linked child'}
          </div>
        </div>
      )}
    </div>
  );
}
